import { useEffect, useState, useRef } from "react";

const TreeListComponent = ({
  data,
  containerWidth = 400,
  cardStyles = {},
  fieldsConfig = [],
  // Selection state props
  selectedBankId,
  selectedBankIds = [],
  selectedBranchId,
  selectedBranchIds = [],
  expandedBanks,
  // Event handlers
  onBankSelect,
  onBranchSelect,
  handleMultiBankSelect,
  handleMultiBranchSelect,
  setExpandedBanks,
  // Configuration
  isMultiSelectionMode = true,
  getThemeURL,
  getData,
  selectedDefaultBankId,
}) => {
  // State for user data and container measurements
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [defaultSelectionApplied, setDefaultSelectionApplied] = useState(false);

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const userResponse = await getData(getThemeURL);
        if (
          userResponse &&
          Array.isArray(userResponse) &&
          userResponse.length > 0
        ) {
          const user = userResponse[0];
          setUserProfile(user);
          console.log("User profile fetched:", user);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Track container dimensions for proper height calculation
  useEffect(() => {
    const updateContainerDimensions = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setContainerDimensions({ width: clientWidth, height: clientHeight });
      }
    };

    // Initial measurement
    updateContainerDimensions();

    // Setup resize observer for more accurate tracking
    const resizeObserver = new ResizeObserver(updateContainerDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // // Function to check if a field should be excluded from display
  // const shouldExcludeField = (key) => {
  //   const excludedFields = [
  //     'bank_id', 'bankId', 'Bank_ID',
  //     'branch_id', 'branchId', 'Branch_ID', 'branch_details_id'
  //   ];
  //   return excludedFields.includes(key);
  // };

  // Function to check if a field is enabled in fieldsConfig
  const isFieldEnabled = (key) => {
    // If no fieldsConfig exists, show all fields (backward compatibility)
    if (!fieldsConfig || fieldsConfig.length === 0) {
      return true;
    }

    // Find the field configuration
    const fieldConfig = fieldsConfig.find((config) => config.fieldName === key);

    // If field is not in configuration, assume it's enabled (for ID fields, etc.)
    if (!fieldConfig) {
      return true;
    }

    // Return the enabled status
    return fieldConfig.enabled;
  };

  // Function to get styles for a field
  const getStyleForField = (fieldName) => {
    const color = cardStyles[`${fieldName}_color`] || "#374151";
    const fontSize = cardStyles[`${fieldName}_fontSize`] || 12;
    const textAlignment = cardStyles[`${fieldName}_textAlignment`] || "left";

    return {
      color,
      fontSize: `${fontSize}px`,
      textAlign: textAlignment,
    };
  };

  // Process data directly without useMemo
  const processGroupedData = () => {
    if (!data || data.length === 0) return [];

    const groups = {};

    // Filter data based on field configuration before grouping
    const filteredData = data.map((item) => {
      const filteredItem = {};

      // Include all fields for processing, but filter display fields later
      Object.keys(item).forEach((key) => {
        // Always include ID fields for functionality
        if (
          key === "bank_id" ||
          key === "branch_details_id" ||
          key === "branch_id" ||
          key === "id"
        ) {
          filteredItem[key] = item[key];
        }
        // Include other fields based on configuration
        else if (isFieldEnabled(key)) {
          filteredItem[key] = item[key];
        }
        // Always include essential fields for bank list functionality
        else if (
          [
            "bank_name",
            "bank_city",
            "city",
            "bank_active_status",
            "active_status",
            "branch_name",
            "branch_details_address_line1",
            "branch_details_address_line2",
            "branch_details_active_status",
            "description",
            "district",
            "mandal",
            "latitude",
            "longitude",
          ].includes(key)
        ) {
          filteredItem[key] = item[key];
        }
      });

      return filteredItem;
    });

    filteredData.forEach((item) => {
      const bankId = item.bank_id || item.id; // Handle both bank_id and id fields
      if (!groups[bankId]) {
        groups[bankId] = {
          bank_id: bankId,
          bank_name: item.bank_name,
          bank_city: item.bank_city || item.city, // Handle both bank_city and city
          bank_active_status:
            item.bank_active_status !== undefined
              ? item.bank_active_status
              : item.active_status, // Handle both field names
          branches: [],
        };
      }

      // Only add to branches if there's branch data
      if (item.branch_name || item.branch_details_id || item.branch_id) {
        groups[bankId].branches.push(item);
      }
    });

    // Convert to array and sort by bank_id
    return Object.values(groups).sort((a, b) => a.bank_id - b.bank_id);
  };

  const groupedData = processGroupedData();

  // Check if there's only one bank with one branch
  const isSingleItem =
    groupedData.length === 1 && groupedData[0].branches.length === 1;

  // Check if there's only one bank (regardless of branch count)
  const isSingleBank = groupedData.length === 1;

  // Check if user is Bank User - this determines read-only mode
  const isBankUser = userProfile?.role_name === "Bank User";

  // Helper function to calculate instance bank IDs based on selected branches
  const getInstanceBankIds = (selectedBranchIds, groupedData) => {
    const instanceBankIds = new Set();

    // Find all banks that have at least one selected branch
    groupedData.forEach((bank) => {
      const hasBranchSelected = bank.branches.some((branch) => {
        const branchId = branch.branch_details_id || branch.branch_id;
        return selectedBranchIds.includes(branchId);
      });

      if (hasBranchSelected) {
        instanceBankIds.add(bank.bank_id);
      }
    });

    return Array.from(instanceBankIds);
  };

  // Apply default bank and branch selection based on selectedDefaultBankId
  useEffect(() => {
    console.log("Default selection effect triggered:", {
      loading,
      userProfile: !!userProfile,
      roleName: userProfile?.role_name,
      selectedDefaultBankId,
      defaultSelectionApplied,
      groupedDataLength: groupedData.length,
      isMultiSelectionMode,
      hasHandlers: !!(handleMultiBankSelect && handleMultiBranchSelect),
    });

    if (
      !loading &&
      userProfile &&
      userProfile.role_name === "SUPER ADMIN" &&
      selectedDefaultBankId !== null &&
      selectedDefaultBankId !== undefined &&
      !defaultSelectionApplied &&
      groupedData.length > 0 &&
      isMultiSelectionMode &&
      handleMultiBankSelect &&
      handleMultiBranchSelect
    ) {
      // Find the default bank - convert both to numbers for comparison
      const defaultBank = groupedData.find(
        (bank) => Number(bank.bank_id) === Number(selectedDefaultBankId)
      );

      console.log("Looking for default bank:", {
        selectedDefaultBankId,
        availableBanks: groupedData.map((b) => ({
          id: b.bank_id,
          name: b.bank_name,
        })),
        foundBank: defaultBank,
      });

      if (defaultBank) {
        console.log(
          `Found and applying default selection for bank ID: ${selectedDefaultBankId}`,
          defaultBank
        );

        // Get all branch IDs for the default bank
        const defaultBranchIds = defaultBank.branches
          .map((branch) => branch.branch_details_id || branch.branch_id)
          .filter(Boolean);

        console.log("Default bank branches:", defaultBranchIds);

        // Update bank selection - ensure we're working with current state
        const currentSelectedBankIds = Array.isArray(selectedBankIds)
          ? [...selectedBankIds]
          : [];
        const newSelectedBankIds = [...currentSelectedBankIds];

        if (!newSelectedBankIds.includes(Number(selectedDefaultBankId))) {
          newSelectedBankIds.push(Number(selectedDefaultBankId));
        }

        // Update branch selection - ensure we're working with current state
        const currentSelectedBranchIds = Array.isArray(selectedBranchIds)
          ? [...selectedBranchIds]
          : [];
        const newSelectedBranchIds = [...currentSelectedBranchIds];

        defaultBranchIds.forEach((branchId) => {
          if (!newSelectedBranchIds.includes(branchId)) {
            newSelectedBranchIds.push(branchId);
          }
        });

        // Calculate instance bank IDs
        const instanceBankIds = getInstanceBankIds(
          newSelectedBranchIds,
          groupedData
        );

        // Prepare selection data
        const selectedBanks = groupedData.filter((b) =>
          newSelectedBankIds.includes(b.bank_id)
        );
        const selectedBranches = groupedData
          .flatMap((b) => b.branches)
          .filter((branch) =>
            newSelectedBranchIds.includes(
              branch.branch_details_id || branch.branch_id
            )
          );

        const selectionData = {
          banks: selectedBanks,
          branches: selectedBranches,
          instanceBankIds: instanceBankIds,
        };

        console.log("Calling selection handlers with:", {
          bankIds: newSelectedBankIds,
          branchIds: newSelectedBranchIds,
          instanceBankIds: instanceBankIds,
          selectionData,
        });

        // Call the selection handlers
        try {
          handleMultiBankSelect(newSelectedBankIds, selectedBanks);
          handleMultiBranchSelect(
            newSelectedBankIds,
            newSelectedBranchIds,
            selectionData
          );

          // Mark default selection as applied
          setDefaultSelectionApplied(true);

          console.log("✅ Default selection successfully applied");
        } catch (error) {
          console.error("Error applying default selection:", error);
        }
      } else {
        console.warn(
          `⚠️ Default bank with ID ${selectedDefaultBankId} not found in data`
        );
      }
    }
  }, [
    loading,
    userProfile,
    selectedDefaultBankId,
    defaultSelectionApplied,
    groupedData.length, // Use length instead of the full array to prevent infinite loops
    isMultiSelectionMode,
    handleMultiBankSelect,
    handleMultiBranchSelect,
    // Removed selectedBankIds and selectedBranchIds from dependencies to prevent conflicts
  ]);

  // CALCULATE LAYOUT METRICS FUNCTION - DEFINED BEFORE USE
  const calculateLayoutMetrics = () => {
    if (
      !userProfile ||
      (userProfile.is_admin !== true &&
        userProfile.role_name === "SUPER ADMIN") ||
      !containerDimensions.height
    ) {
      return {
        needsScroll: false,
        branchHeight: "auto",
        containerOverflow: "auto",
      };
    }

    const fixedCardHeight = 80; // Bank card height
    const debugInfoHeight = 30; // Debug info height
    const padding = 20; // Container padding
    const bankMargin = 12; // Margin between banks
    const branchMargin = 16; // Branch container margins
    const minBranchCardHeight = 104; // Minimum height for each branch card (80px + padding)

    // Calculate total fixed heights
    const totalBanks = groupedData.length;
    const bankCardsHeight = totalBanks * (fixedCardHeight + bankMargin);
    const branchMarginsHeight = totalBanks * branchMargin;
    const totalFixedHeight =
      debugInfoHeight + padding + bankCardsHeight + branchMarginsHeight;

    // Available height for all branches combined
    const availableHeight = containerDimensions.height - totalFixedHeight;

    // Calculate minimum required height for all branches
    const totalBranches = groupedData.reduce(
      (total, bank) => total + bank.branches.length,
      0
    );
    const minRequiredBranchesHeight = totalBranches * minBranchCardHeight;

    const needsScroll = minRequiredBranchesHeight > availableHeight;

    if (needsScroll) {
      // If scrolling is needed, use standard height with overflow
      return {
        needsScroll: true,
        branchHeight: "300px",
        containerOverflow: "auto",
      };
    } else {
      // If no scrolling needed, distribute available height among banks
      const heightPerBank = Math.max(150, availableHeight / totalBanks);
      return {
        needsScroll: false,
        branchHeight: `${heightPerBank}px`,
        containerOverflow: "hidden",
      };
    }
  };

  // BRANCH LAYOUT METRICS FUNCTION - FOR HO USERS
  const calculateBranchLayoutMetrics = (bank, bankIndex) => {
    if (
      !userProfile ||
      (userProfile.is_admin !== true &&
        userProfile.role_name === "SUPER ADMIN") ||
      !containerDimensions.height
    ) {
      return {
        branchNeedsScroll: false,
        branchContainerHeight: "auto",
        branchOverflow: "auto",
      };
    }

    const debugInfoHeight = 30;
    const containerPadding = 20; // 1% padding converted
    const bankCardHeight = 80;
    const bankMargin = 12;
    const branchContainerMarginTop = 8;
    const branchCardHeight = 104; // Each branch card height + margin
    const branchCardMargin = 8;

    // Calculate space used by all bank cards
    const totalBanks = groupedData.length;
    const allBankCardsHeight = totalBanks * (bankCardHeight + bankMargin);

    // Calculate space used by other expanded banks' branch containers
    const otherExpandedBanks = groupedData.filter(
      (b, idx) =>
        idx !== bankIndex && expandedBanks && expandedBanks.has(b.bank_id)
    );
    const otherBranchContainersHeight =
      otherExpandedBanks.length * branchContainerMarginTop;

    // Space used by fixed elements
    const fixedHeight =
      debugInfoHeight +
      containerPadding +
      allBankCardsHeight +
      branchContainerMarginTop +
      otherBranchContainersHeight;

    // Available height for this branch container
    const availableHeight = containerDimensions.height - fixedHeight;

    // Required height for all branches in this bank
    const requiredBranchesHeight =
      bank.branches.length * (branchCardHeight + branchCardMargin);

    const branchNeedsScroll = requiredBranchesHeight > availableHeight;

    if (branchNeedsScroll) {
      // Use available height with scrolling
      return {
        branchNeedsScroll: true,
        branchContainerHeight: `${Math.max(200, availableHeight)}px`,
        branchOverflow: "auto",
      };
    } else {
      // Fit all branches without scrolling
      return {
        branchNeedsScroll: false,
        branchContainerHeight: `${Math.max(150, availableHeight)}px`,
        branchOverflow: "hidden",
      };
    }
  };

  // CALL LAYOUT METRICS FUNCTION AFTER DEFINITION
  const layoutMetrics = calculateLayoutMetrics();

  // Auto-expand logic based on user role and single item
  useEffect(() => {
    if (
      loading ||
      !userProfile ||
      !setExpandedBanks ||
      groupedData.length === 0
    ) {
      return;
    }

    const newExpanded = new Set();

    // Auto-expand for HO role, Bank User role, or single item
    if (userProfile.is_admin === true || isBankUser || isSingleItem) {
      // Expand all banks for HO role, Bank User role, or single bank for single item
      groupedData.forEach((bank) => {
        newExpanded.add(bank.bank_id);
      });

      setExpandedBanks(newExpanded);
      console.log(
        `Auto-expanded banks for ${userProfile.role_name}:`,
        Array.from(newExpanded)
      );
    }
    // Auto-expand the default selected bank
    else if (selectedDefaultBankId && !defaultSelectionApplied) {
      const defaultBank = groupedData.find(
        (bank) => bank.bank_id === selectedDefaultBankId
      );
      if (defaultBank) {
        newExpanded.add(selectedDefaultBankId);
        // Keep any existing expanded banks
        if (expandedBanks) {
          expandedBanks.forEach((bankId) => newExpanded.add(bankId));
        }
        setExpandedBanks(newExpanded);
        console.log(`Auto-expanded default bank: ${selectedDefaultBankId}`);
      }
    }
  }, [
    loading,
    userProfile,
    groupedData.length,
    isSingleItem,
    isBankUser,
    setExpandedBanks,
    selectedDefaultBankId,
    defaultSelectionApplied,
  ]);

  // Helper functions for selection state
  const isBankSelected = (bankId) => {
    return selectedBankId === bankId || selectedBankIds.includes(bankId);
  };

  const isBranchSelected = (branchId) => {
    return (
      selectedBranchId === branchId || selectedBranchIds.includes(branchId)
    );
  };

  // Check if all branches are selected for a bank
  const areAllBranchesSelected = (bankId, branches) => {
    return branches.every((branch) =>
      selectedBranchIds.includes(branch.branch_details_id || branch.branch_id)
    );
  };

  // Handle single bank selection (only bank, no branch interaction)
  const handleSingleBankSelect = (bank) => {
    // Disable selection for single item or Bank User
    if (isSingleItem || isBankUser) return;

    const bankId = bank.bank_id;

    // Only toggle bank selection - no branch interaction
    if (onBankSelect) {
      onBankSelect(isBankSelected(bankId) ? null : bankId, bank);
    }
  };

  // Handle single branch selection (only branch, no bank interaction)
  const handleSingleBranchSelect = (branch, bank) => {
    // Disable selection for single item or Bank User
    if (isSingleItem || isBankUser) return;

    const branchId = branch.branch_details_id || branch.branch_id;

    // Only toggle branch selection - no bank interaction
    if (onBranchSelect) {
      const newBranchId = isBranchSelected(branchId) ? null : branchId;
      onBranchSelect(newBranchId, branch, null);
    }
  };

  // Handle multi-bank selection (only banks, no branch interaction)
  const handleMultiBankToggle = (bank) => {
    // Disable selection for single item or Bank User
    if (isSingleItem || isBankUser) return;

    const bankId = bank.bank_id;
    let newSelectedBankIds = [...(selectedBankIds || [])];

    console.log("🏦 Bank Toggle START:", {
      clickedBankId: bankId,
      currentSelectedBankIds: selectedBankIds,
      currentSelectedBranchIds: selectedBranchIds,
    });

    if (newSelectedBankIds.includes(bankId)) {
      // Remove bank
      newSelectedBankIds = newSelectedBankIds.filter((id) => id !== bankId);
    } else {
      // Add bank
      newSelectedBankIds.push(bankId);
    }

    console.log("🏦 Bank Toggle - New selected bank IDs:", newSelectedBankIds);

    // Call bank selection handler only - no branch interaction
    if (handleMultiBankSelect) {
      const selectedBanks = groupedData.filter((b) =>
        newSelectedBankIds.includes(b.bank_id)
      );
      console.log(
        "📞 Calling handleMultiBankSelect with:",
        newSelectedBankIds,
        selectedBanks
      );
      handleMultiBankSelect(newSelectedBankIds, selectedBanks);
    }

    // Calculate instance bank IDs: directly selected banks + banks with selected branches
    if (handleMultiBranchSelect) {
      const currentSelectedBranchIds = [...(selectedBranchIds || [])];
      const branchParentBankIds = getInstanceBankIds(
        currentSelectedBranchIds,
        groupedData
      );

      // Combine directly selected bank IDs with banks that have selected branches
      const combinedInstanceBankIds = [
        ...new Set([...newSelectedBankIds, ...branchParentBankIds]),
      ];

      const selectionData = {
        banks: groupedData.filter((b) =>
          newSelectedBankIds.includes(b.bank_id)
        ),
        branches: groupedData
          .flatMap((b) => b.branches)
          .filter((branch) =>
            currentSelectedBranchIds.includes(
              branch.branch_details_id || branch.branch_id
            )
          ),
        instanceBankIds: combinedInstanceBankIds,
      };

      console.log("🏦 Bank Toggle - Calculated instanceBankIds:", {
        newSelectedBankIds,
        currentSelectedBranchIds,
        branchParentBankIds,
        combinedInstanceBankIds,
        "SENDING TO PARENT": combinedInstanceBankIds,
      });

      handleMultiBranchSelect(
        newSelectedBankIds,
        currentSelectedBranchIds,
        selectionData
      );
    }
  };

  // Handle "Select All Branches" - only selects branches, NO bank auto-selection
  const handleSelectAllBranches = (bank, isChecked) => {
    // Disable selection for single item or Bank User
    if (isSingleItem || isBankUser) return;

    const branchIds = bank.branches.map(
      (branch) => branch.branch_details_id || branch.branch_id
    );

    // Keep current bank selections unchanged
    const currentSelectedBankIds = [...(selectedBankIds || [])];
    let newSelectedBranchIds = [...(selectedBranchIds || [])];

    console.log("🔍 SelectAllBranches BEFORE:", {
      bankId: bank.bank_id,
      isChecked,
      "selectedBankIds PROP from parent": selectedBankIds,
      currentSelectedBankIds,
      currentSelectedBranchIds: selectedBranchIds,
      branchIdsToToggle: branchIds,
    });

    if (isChecked) {
      // Add all branch IDs - NO bank selection
      branchIds.forEach((branchId) => {
        if (!newSelectedBranchIds.includes(branchId)) {
          newSelectedBranchIds.push(branchId);
        }
      });
    } else {
      // Remove all branches from this bank
      branchIds.forEach((branchId) => {
        newSelectedBranchIds = newSelectedBranchIds.filter(
          (id) => id !== branchId
        );
      });
    }

    // THIS IS THE KEY FIX: Calculate banks with selected branches
    const branchParentBankIds = getInstanceBankIds(
      newSelectedBranchIds,
      groupedData
    );

    // Combine directly selected bank IDs with banks that have selected branches
    const combinedInstanceBankIds = [
      ...new Set([...currentSelectedBankIds, ...branchParentBankIds]),
    ];

    console.log("✅ SelectAllBranches AFTER calculation:", {
      newSelectedBranchIds,
      directlySelectedBanks: currentSelectedBankIds,
      banksWithSelectedBranches: branchParentBankIds,
      combinedInstanceBankIds,
      explanation: {
        scenario1_bankChecked: currentSelectedBankIds.includes(bank.bank_id)
          ? "Bank checkbox IS checked - should be in instanceBankIds"
          : "Bank checkbox NOT checked",
        scenario2_branchesChecked: branchParentBankIds.includes(bank.bank_id)
          ? "At least one branch IS checked - should be in instanceBankIds"
          : "No branches checked",
        result: combinedInstanceBankIds.includes(bank.bank_id)
          ? "✓ Bank IS in instanceBankIds"
          : "✗ Bank NOT in instanceBankIds",
      },
    });

    if (handleMultiBranchSelect) {
      const selectionData = {
        banks: groupedData.filter((b) =>
          currentSelectedBankIds.includes(b.bank_id)
        ),
        branches: groupedData
          .flatMap((b) => b.branches)
          .filter((branch) =>
            newSelectedBranchIds.includes(
              branch.branch_details_id || branch.branch_id
            )
          ),
        instanceBankIds: combinedInstanceBankIds, // USE COMBINED, NOT JUST branchParentBankIds
      };

      console.log("📤 Calling handleMultiBranchSelect with:", {
        bankIds: currentSelectedBankIds,
        branchIds: newSelectedBranchIds,
        instanceBankIds: combinedInstanceBankIds,
      });

      handleMultiBranchSelect(
        currentSelectedBankIds,
        newSelectedBranchIds,
        selectionData
      );
    }
  };

  // Handle individual branch checkbox (only branch, no bank interaction)
  const handleBranchCheckboxToggle = (branch, bank) => {
    // Disable selection for single item or Bank User
    if (isSingleItem || isBankUser) return;

    const branchId = branch.branch_details_id || branch.branch_id;

    // Keep current bank selections unchanged
    const currentSelectedBankIds = [...(selectedBankIds || [])];
    let newSelectedBranchIds = [...(selectedBranchIds || [])];

    console.log("🔍 BranchCheckboxToggle BEFORE:", {
      branchId,
      bankId: bank.bank_id,
      "selectedBankIds PROP from parent": selectedBankIds,
      currentSelectedBankIds,
      currentSelectedBranchIds: selectedBranchIds,
    });

    if (newSelectedBranchIds.includes(branchId)) {
      // Remove branch only
      newSelectedBranchIds = newSelectedBranchIds.filter(
        (id) => id !== branchId
      );
    } else {
      // Add branch only
      newSelectedBranchIds.push(branchId);
    }

    // THIS IS THE KEY FIX: Calculate banks with selected branches
    const branchParentBankIds = getInstanceBankIds(
      newSelectedBranchIds,
      groupedData
    );

    // Combine directly selected bank IDs with banks that have selected branches
    const combinedInstanceBankIds = [
      ...new Set([...currentSelectedBankIds, ...branchParentBankIds]),
    ];

    console.log("✅ BranchCheckboxToggle AFTER calculation:", {
      newSelectedBranchIds,
      directlySelectedBanks: currentSelectedBankIds,
      banksWithSelectedBranches: branchParentBankIds,
      combinedInstanceBankIds,
      explanation: {
        scenario1_bankChecked: currentSelectedBankIds.includes(bank.bank_id)
          ? "Bank checkbox IS checked - should be in instanceBankIds"
          : "Bank checkbox NOT checked",
        scenario2_branchesChecked: branchParentBankIds.includes(bank.bank_id)
          ? "At least one branch IS checked - should be in instanceBankIds"
          : "No branches checked for this bank",
        result: combinedInstanceBankIds.includes(bank.bank_id)
          ? "✓ Bank IS in instanceBankIds"
          : "✗ Bank NOT in instanceBankIds",
      },
    });

    if (handleMultiBranchSelect) {
      const selectionData = {
        banks: groupedData.filter((b) =>
          currentSelectedBankIds.includes(b.bank_id)
        ),
        branches: groupedData
          .flatMap((b) => b.branches)
          .filter((branch) =>
            newSelectedBranchIds.includes(
              branch.branch_details_id || branch.branch_id
            )
          ),
        instanceBankIds: combinedInstanceBankIds, // USE COMBINED, NOT JUST branchParentBankIds
      };

      console.log("📤 Calling handleMultiBranchSelect with:", {
        bankIds: currentSelectedBankIds,
        branchIds: newSelectedBranchIds,
        "selectionData.instanceBankIds": selectionData.instanceBankIds,
        fullSelectionData: selectionData,
      });

      handleMultiBranchSelect(
        currentSelectedBankIds,
        newSelectedBranchIds,
        selectionData
      );
    }
  };

  // Show loading state while fetching user data
  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          height: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "white",
        }}
      >
        <div
          style={{
            fontSize: "14px",
            color: "#6b7280",
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="tree-list-main-container"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: "1%",
        backgroundColor: "white",
        minHeight: "200px",
        position: "relative",
      }}
    >
      {/* Main content container - Same logic as original for banks */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY:
            userProfile?.is_admin === true
              ? layoutMetrics.needsScroll
                ? "auto"
                : "hidden"
              : "auto",
          overflowX: "hidden",
          paddingRight: layoutMetrics.needsScroll ? "8px" : "0px",
          minHeight: 0,
        }}
      >
        {groupedData.map((bank, index) => {
          const rank = index + 1;
          const isExpanded = expandedBanks && expandedBanks.has(bank.bank_id);
          const isBankSelectedState = isBankSelected(bank.bank_id);
          const allBranchesSelected = areAllBranchesSelected(
            bank.bank_id,
            bank.branches
          );

          // Calculate branch layout metrics for this specific bank
          const branchMetrics = calculateBranchLayoutMetrics(bank, index);

          // Calculate responsive dimensions
          const itemPadding = Math.max(8, containerWidth * 0.02);
          const rankBadgeSize = Math.max(
            24,
            Math.min(50, containerWidth * 0.08)
          );
          const fixedCardHeight = 80;

          // Get styles for bank fields
          const bankNameStyle = getStyleForField("bank_name");
          const bankCityStyle = getStyleForField("bank_city");
          const bankActiveStatusStyle = getStyleForField("bank_active_status");

          return (
            <div
              key={bank.bank_id}
              style={{
                marginBottom: "12px",
                flex:
                  userProfile?.is_admin === true && !layoutMetrics.needsScroll
                    ? "1 1 0"
                    : "none",
                display: "flex",
                flexDirection: "column",
                minHeight:
                  userProfile?.is_admin === true && !layoutMetrics.needsScroll
                    ? "200px"
                    : "auto",
              }}
            >
              {/* Main Bank Card */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: `${itemPadding}px`,
                  backgroundColor:
                    cardStyles.bank_card_color ||
                    (isBankSelectedState ? "#e0f2fe" : "white"),
                  borderRadius: `${Math.max(6, containerWidth * 0.02)}px`,
                  boxShadow: isBankSelectedState
                    ? "0 8px 16px rgba(0, 123, 255, 0.3)"
                    : "0 4px 8px rgba(0, 0, 0, 0.12)",
                  border: isBankSelectedState
                    ? "2px solid #1a7a73"
                    : "1px solid #e5e7eb",
                  transition: "all 0.3s ease",
                  cursor: isSingleItem || isBankUser ? "default" : "pointer",
                  height: `${fixedCardHeight}px`,
                  minHeight: `${fixedCardHeight}px`,
                  maxHeight: `${fixedCardHeight}px`,
                  overflow: "hidden",
                  flexShrink: 0,
                  opacity: isSingleItem || isBankUser ? 0.9 : 1,
                }}
                onClick={() => {
                  if (isSingleItem || isBankUser) return;

                  if (isMultiSelectionMode) {
                    handleMultiBankToggle(bank);
                  } else {
                    handleSingleBankSelect(bank);
                  }
                }}
                onMouseOver={(e) => {
                  if (!isBankSelectedState && !isSingleItem && !isBankUser) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 16px rgba(0, 0, 0, 0.18)";
                  }
                }}
                onMouseOut={(e) => {
                  if (!isBankSelectedState && !isSingleItem && !isBankUser) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 8px rgba(0, 0, 0, 0.12)";
                  }
                }}
              >
                {/* Multi-selection checkbox for bank - Hide for single item and Bank User */}
                {isMultiSelectionMode && !isSingleItem && !isBankUser && (
                  <div
                    style={{
                      marginRight: "12px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedBankIds.includes(bank.bank_id)}
                      onChange={() => handleMultiBankToggle(bank)}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                )}

                {/* Bank Details */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    overflow: "hidden",
                    height: "100%",
                    justifyContent: "center",
                  }}
                >
                  {/* Bank Name - Only show if enabled with tooltip */}
                  {isFieldEnabled("bank_name") && (
                    <div
                      style={{
                        fontSize:
                          bankNameStyle.fontSize ||
                          `clamp(12px, ${Math.max(
                            12,
                            containerWidth * 0.025
                          )}px, 16px)`,
                        fontWeight: "700",
                        color:
                          bankNameStyle.color ||
                          (isBankSelectedState ? "#1a7a73" : "#1f2937"),
                        textAlign: bankNameStyle.textAlign,
                        lineHeight: "1.2",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        position: "relative",
                      }}
                      title={bank.bank_name}
                    >
                      {bank.bank_name}
                    </div>
                  )}

                  {/* Bank City and Branch Count - Only show if enabled */}
                  {isFieldEnabled("bank_city") && bank.bank_city && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: `${Math.max(6, containerWidth * 0.012)}px`,
                        fontSize:
                          bankCityStyle.fontSize ||
                          `clamp(10px, ${Math.max(
                            10,
                            containerWidth * 0.02
                          )}px, 14px)`,
                        color:
                          bankCityStyle.color ||
                          (isBankSelectedState ? "#1a7a73" : "#6b7280"),
                        textAlign: bankCityStyle.textAlign,
                        overflow: "hidden",
                      }}
                    >
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {bank.bank_city}
                      </span>
                    </div>
                  )}

                  {/* Bank Active Status - Only show if enabled */}
                  {isFieldEnabled("bank_active_status") &&
                    bank.hasOwnProperty("bank_active_status") &&
                    bank.bank_active_status !== undefined &&
                    bank.bank_active_status !== null && (
                      <div
                        style={{
                          fontSize: bankActiveStatusStyle.fontSize || "10px",
                          color:
                            bankActiveStatusStyle.color ||
                            (bank.bank_active_status ? "#10b981" : "#ef4444"),
                          textAlign: bankActiveStatusStyle.textAlign,
                          fontWeight: "500",
                        }}
                      >
                        {bank.bank_active_status ? "Active" : "Inactive"}
                      </div>
                    )}
                </div>

                {/* Select All Branches Checkbox - Hide for single item and Bank User */}
                {isMultiSelectionMode && !isSingleItem && !isBankUser && (
                  <div
                    style={{
                      marginLeft: "8px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "10px",
                        color: "#6b7280",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={areAllBranchesSelected(
                          bank.bank_id,
                          bank.branches
                        )}
                        onChange={(e) => {
                          console.log(
                            "All Branches clicked for bank:",
                            bank.bank_id,
                            "checked:",
                            e.target.checked
                          );
                          handleSelectAllBranches(bank, e.target.checked);
                        }}
                        style={{
                          width: "14px",
                          height: "14px",
                          cursor: "pointer",
                        }}
                      />
                      All Branches
                    </label>
                  </div>
                )}

                {/* Expand/Collapse Button - Hide for single item and Bank User since they're auto-expanded */}
                {!isSingleItem && !isBankUser && (
                  <div
                    style={{
                      marginLeft: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      color:
                        cardStyles.expand_color ||
                        (isBankSelectedState ? "#1a7a73" : "#6b7280"),
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (setExpandedBanks) {
                        const newExpanded = new Set(expandedBanks);
                        if (isExpanded) {
                          newExpanded.delete(bank.bank_id);
                        } else {
                          newExpanded.add(bank.bank_id);
                        }
                        setExpandedBanks(newExpanded);
                      }
                    }}
                  >
                    {isExpanded ? (
                      <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                        -
                      </span>
                    ) : (
                      <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                        +
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Branch Container - Only show if expanded and has branches */}
              {(isExpanded || isSingleItem) &&
                bank.branches &&
                bank.branches.length > 0 && (
                  <div
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      flex:
                        userProfile?.is_admin === true &&
                        !layoutMetrics.needsScroll
                          ? "1 1 0"
                          : "none",
                    }}
                  >
                    <div
                      style={{
                        height:
                          userProfile?.is_admin === true
                            ? branchMetrics.branchContainerHeight
                            : "auto",
                        maxHeight:
                          userProfile?.is_admin === true
                            ? branchMetrics.branchContainerHeight
                            : "300px",
                        overflowY:
                          userProfile?.is_admin === true
                            ? branchMetrics.branchOverflow
                            : "auto",
                        overflowX: "hidden",
                        paddingRight:
                          userProfile?.is_admin === true &&
                          branchMetrics.branchNeedsScroll
                            ? "8px"
                            : "0px",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#d1d5db #f3f4f6",
                        flex:
                          userProfile?.is_admin === true &&
                          !branchMetrics.branchNeedsScroll
                            ? "1 1 0"
                            : "none",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {bank.branches.map((branch, branchIndex) => {
                        const branchId =
                          branch.branch_details_id || branch.branch_id;
                        const isBranchSelectedState =
                          isBranchSelected(branchId);

                        // Get styles for branch fields
                        const branchNameStyle = getStyleForField("branch_name");
                        const branchAddressStyle = getStyleForField(
                          "branch_details_address_line1"
                        );
                        const branchActiveStatusStyle = getStyleForField(
                          "branch_details_active_status"
                        );

                        return (
                          <div
                            key={branchId || branchIndex}
                            style={{
                              padding: "12px",
                              marginBottom: "8px",
                              marginLeft: "10px",
                              backgroundColor:
                                cardStyles.branch_card_color ||
                                (isBranchSelectedState ? "#ccfbf1" : "#f9fafb"),
                              borderRadius: "6px",
                              border: isBranchSelectedState
                                ? "2px solid #1a7a73"
                                : "1px solid #e5e7eb",
                              minHeight: "80px",
                              cursor:
                                isSingleItem || isBankUser
                                  ? "default"
                                  : "pointer",
                              transition: "all 0.2s ease",
                              boxShadow: isBranchSelectedState
                                ? "0 4px 8px rgba(59, 130, 246, 0.2)"
                                : "none",
                              display: "flex",
                              alignItems: "center",
                              opacity: isSingleItem || isBankUser ? 0.9 : 1,
                              flexShrink: 0,
                              width: "95%",
                              maxWidth: "100%",
                              boxSizing: "border-box",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();

                              if (isSingleItem || isBankUser) return;

                              if (isMultiSelectionMode) {
                                handleBranchCheckboxToggle(branch, bank);
                              } else {
                                handleSingleBranchSelect(branch, bank);
                              }
                            }}
                            onMouseOver={(e) => {
                              if (
                                !isBranchSelectedState &&
                                !isSingleItem &&
                                !isBankUser
                              ) {
                                e.currentTarget.style.transform =
                                  "translateX(4px)";
                              }
                            }}
                            onMouseOut={(e) => {
                              if (
                                !isBranchSelectedState &&
                                !isSingleItem &&
                                !isBankUser
                              ) {
                                e.currentTarget.style.transform =
                                  "translateX(0)";
                              }
                            }}
                          >
                            {/* Branch checkbox - Hide for single item and Bank User */}
                            {isMultiSelectionMode &&
                              !isSingleItem &&
                              !isBankUser && (
                                <div
                                  style={{
                                    marginRight: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isBranchSelectedState}
                                    onChange={() =>
                                      handleBranchCheckboxToggle(branch, bank)
                                    }
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      cursor: "pointer",
                                    }}
                                  />
                                </div>
                              )}

                            <div
                              style={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                              }}
                            >
                              {/* Branch Name - Only show if enabled with tooltip */}
                              {isFieldEnabled("branch_name") && (
                                <div
                                  style={{
                                    fontSize:
                                      branchNameStyle.fontSize ||
                                      `clamp(11px, ${Math.max(
                                        11,
                                        containerWidth * 0.022
                                      )}px, 15px)`,
                                    fontWeight: "600",
                                    color:
                                      branchNameStyle.color ||
                                      (isBranchSelectedState
                                        ? "#1a7a73"
                                        : "#374151"),
                                    textAlign: branchNameStyle.textAlign,
                                    marginBottom: "4px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    title={`Branch: ${branch.branch_name}`}
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      flex: 1,
                                    }}
                                  >
                                    Branch: {branch.branch_name}
                                  </span>
                                </div>
                              )}

                              {/* Branch Address - Only show if enabled */}
                              {isFieldEnabled(
                                "branch_details_address_line1"
                              ) && (
                                <div
                                  style={{
                                    fontSize:
                                      branchAddressStyle.fontSize ||
                                      `clamp(9px, ${Math.max(
                                        9,
                                        containerWidth * 0.018
                                      )}px, 13px)`,
                                    color:
                                      branchAddressStyle.color ||
                                      (isBranchSelectedState
                                        ? "#0f4a45"
                                        : "#6b7280"),
                                    textAlign: branchAddressStyle.textAlign,
                                    lineHeight: "1.4",
                                    marginBottom: "4px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {[
                                    branch.branch_details_address_line1,
                                    branch.branch_details_address_line2,
                                  ]
                                    .filter(Boolean)
                                    .join(", ")}
                                </div>
                              )}

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  {/* Branch Active Status - Only show if enabled */}
                                  {isFieldEnabled(
                                    "branch_details_active_status"
                                  ) &&
                                    branch.hasOwnProperty(
                                      "branch_details_active_status"
                                    ) && (
                                      <span
                                        style={{
                                          fontSize:
                                            branchActiveStatusStyle.fontSize ||
                                            `clamp(8px, ${Math.max(
                                              8,
                                              containerWidth * 0.016
                                            )}px, 12px)`,
                                          color:
                                            branchActiveStatusStyle.color ||
                                            (branch.branch_details_active_status
                                              ? "#10b981"
                                              : "#ef4444"),
                                          textAlign:
                                            branchActiveStatusStyle.textAlign,
                                          fontWeight: "500",
                                        }}
                                      >
                                        {branch.branch_details_active_status
                                          ? "Active"
                                          : "Inactive"}
                                      </span>
                                    )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TreeListComponent;
